import {
  SET_DATA_QUALITY_LOADING_STATE,
  SET_DATA_QUALITY,
  SET_BAR_CHART_DATA,
  CLOSE_VALIDITY_DRAWER,
  OPEN_VALIDITY_DRAWER,
  UPDATE_VALIDITY_DRAWER,
  UPDATE_CHARTS,
  SET_INITIAL_PERIOD_BAR_CHART_DATA,
  CHANGE_CURRENT_PAGE,
  SET_FILTERS,
} from '@/store/modules/data-quality/mutation-types';
import {cloneDeep} from 'lodash-es';
import moment from 'moment';
import {axiosClient} from '@/core/services/http.service';
import { calculateTotalValidityData } from '@/core/utils/calculateTotalValidityData';
import { filterForDataQuality } from '@/core/utils/filterForDataQuality';

const transformPeriodChartData = (data) => {
  const groupedData = {};

  data.forEach((item) => {
    const date = item.recordDate;
    
    if (!groupedData[date]) {
      groupedData[date] = {
        confirmedRecords: 0,
        validatedRecords: 0,
        validationFailedRecords: 0,
      };
    }
    
    groupedData[date].confirmedRecords += item.confirmedCount || 0;
    groupedData[date].validatedRecords += item.validatedCount || 0;
    groupedData[date].validationFailedRecords += item.validationFailedCount || 0;
  });

  const groupedLabels = Object.keys(groupedData);
  const groupedConfirmedRecords = groupedLabels.map((date) => groupedData[date].confirmedRecords);
  const groupedValidatedRecords = groupedLabels.map((date) => groupedData[date].validatedRecords);
  const groupedValidationFailedRecords = groupedLabels.map((date) => groupedData[date].validationFailedRecords);

  return {
    labels: groupedLabels,
    confirmedRecords: groupedConfirmedRecords,
    validatedRecords: groupedValidatedRecords,
    validationFailedRecords: groupedValidationFailedRecords,
  };
};

const transformDoughnutData = (data) => {
  const totalData = calculateTotalValidityData(data); 
  return {
    main: {...totalData},
  };
};

export const getBarChartData = async ({commit, rootState}) => {
  commit(SET_DATA_QUALITY_LOADING_STATE, {key: 'barChart', value: true});
  const {success, body} = await axiosClient.get('/api/dataqualitysummary/validationsummary');
  if(success){
    const {main} = transformDoughnutData(body.current);

    const chart = transformPeriodChartData(body.historic);
    const initialPeriodBarChartData = body.historic;
  
    const tags = body.current.flatMap(item => item.tags);
    const origins = body.current.flatMap(item => item.origins);

    // Create the list of views based on the user's data roles
    const userGuid = rootState.user?.currentUserAdpPermission?.data?.userGuid;
    const views = [{ value: 'all', label: 'View: All Tables' }];
    const roleViews = [
      { key: 'owners', label: 'View: My Owner Tables' },
      { key: 'governors', label: 'View: My Governor Tables' },
      { key: 'stewards', label: 'View: My Steward Tables' },
    ];
    roleViews.forEach(({ key, label }) => {
      if (body.current.some(item => item.tags?.some(tag => tag[key]?.includes(userGuid)))) {
        views.push({ value: key, label });
      }
    });
    if (views.length > 1) {
      views.unshift({ value: 'assigned', label: 'View: My Assigned Tables' });
    }
      
    commit(SET_DATA_QUALITY, {
      origins: [...new Set(origins)],
      tags: [...new Set(tags)],
      data: body.current,
      views: views,
    });

    commit(SET_BAR_CHART_DATA, {main, chart});
    commit(SET_INITIAL_PERIOD_BAR_CHART_DATA, initialPeriodBarChartData);
    commit(SET_DATA_QUALITY_LOADING_STATE, {key: 'barChart', value: false});
  }
};

export const getSidebarEntityByGuid = async ({commit, state}, id) => {
  commit(SET_DATA_QUALITY_LOADING_STATE, {key: 'drawer', value: true});
  const {success, body} = await axiosClient.get(`/api/dataqualitysummary/entityvalidationsummary/${id}`);
  if(success){
    commit(UPDATE_VALIDITY_DRAWER, body);
  }
  commit(SET_DATA_QUALITY_LOADING_STATE, {key: 'drawer', value: false});
};

export const openValidityDrawer = async ({commit}, payload) => {
  commit(OPEN_VALIDITY_DRAWER, payload);
};

export const closeValidityDrawer = ({commit}) => {
  commit(CLOSE_VALIDITY_DRAWER);
};

export const updateChartsByFilters = ({commit, state, rootState}) => {
  if(!state.selectedTag && !state.selectedOrigin && !state.searchValue && !state.selectedPeriod && state.currentView.value === 'all') {
    commit(UPDATE_CHARTS, null);
    const {main} = transformDoughnutData(state.qualityTablesData); 
    const chart = transformPeriodChartData(state.initialPeriodBarChartData);
    commit(SET_BAR_CHART_DATA, {main, chart});   
  } else {
    const copyTableData = cloneDeep(state.qualityTablesData);  
    const userGuid = rootState.user?.currentUserAdpPermission?.data?.userGuid;
    const filteredTableData = copyTableData.filter(item => filterForDataQuality({state}, item, userGuid));
    const {main} = transformDoughnutData(filteredTableData);

    const filteredPeriodChartData = state.initialPeriodBarChartData.filter(item => filterForDataQuality({state}, item, userGuid));
    let chart;
    if(!state.selectedPeriod){
      chart = transformPeriodChartData(filteredPeriodChartData);
    }else{
      const currentDate = moment().format('YYYY-MM-DD');
      const startDate = moment(currentDate)
        .subtract(state.selectedPeriod, 1)
        .format('YYYY-MM-DD');
  
      const res = filteredPeriodChartData.filter(item =>
        moment(item.recordDate).isBetween(startDate, currentDate),
      );
      chart = transformPeriodChartData(res);
    }

    commit(SET_BAR_CHART_DATA, {main, chart}); 
    commit(UPDATE_CHARTS, filteredTableData); 
    commit(CHANGE_CURRENT_PAGE, 1);
  }
};

export const setFilters = ({ commit }, filters) => {
  commit(SET_FILTERS, filters);
};